import { useCallback, useMemo, useState } from "react";
import { InitialProctoringState, UnsavedAssessmentInfo } from "../editQuizModalWrapper";
import { VerityApi } from "../../api/fetch";
import {
	convertProctoringAssessmentConfiguration,
	convertProctoringOption,
	getDefaultProctoringOption,
} from "../editQuizHelper";
import { ProctoringAssessmentConfigurationV2, ProctoringOption } from "@verity/proctoring-option/types";
import { coreToast } from "../../core";
import { VerityLogger } from "@yujacorp/verity-logger";
import { DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION } from "@verity/proctoring-option";
import { LiveSessionWithInvitations, PendingLiveSession } from "../live";
import password from "../password";

export type UseEditQuizWrapperProps = {
	api: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	forceThirdParty?: boolean;
	initialPlatformId?: number;
	onInitialOptions?: (
		proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
		proctoringOption: Partial<ProctoringOption>,
	) => void;
	logger?: VerityLogger;
	onEnabledChange: (
		proctoringAssessmentConfiguration: Pick<Partial<ProctoringAssessmentConfigurationV2>, "enabled" | "password">,
		onPasswordGenerated: () => void,
	) => void | Promise<void>;
};

export default function useEditQuizWrapper({
	api,
	quizInfo,
	forceThirdParty,
	initialPlatformId,
	onInitialOptions,
	logger,
	onEnabledChange,
}: UseEditQuizWrapperProps) {
	const {
		getProctoringAssessmentConfiguration,
		areProctoringProfilesEnforced,
		getDefaultProctoringProfile,
		getVerityInstituteSettings,
		validateAssessment,
	} = api;

	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);
	const [defaultProctoringOption, setDefaultProctoringOption] = useState<ProctoringOption | null>(null);
	const [initialEnabledValue, setInitialEnabledValue] = useState<boolean | undefined>(undefined);
	const [initialProctoringAssessmentConfiguration, setInitialProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [initialProctoringOption, setInitialProctoringOption] = useState<InitialProctoringState>();
	const [proctoringOption, setProctoringOption] = useState<Partial<ProctoringOption>>();
	const [currentProfileId, setCurrentProfileId] = useState<number>();
	const [pendingLiveSessions, setPendingLiveSessions] = useState<Record<string, PendingLiveSession>>({});
	const [pendingLiveSessionIds, setPendingLiveSessionIds] = useState<string[]>([]);
	const [existingLiveSessions, setExistingLiveSessions] = useState<Record<string, LiveSessionWithInvitations>>({});

	const refreshSettings = useCallback(
		(confirmedOption?: InitialProctoringState) => {
			void (async () => {
				setEnforcedProfilesOnly(await areProctoringProfilesEnforced());
				const defaultProfile = await getDefaultProctoringProfile();
				const verityInstituteSettings = await getVerityInstituteSettings();
				const defaultProctoringOption = getDefaultProctoringOption(verityInstituteSettings);
				setDefaultProctoringOption(defaultProctoringOption);

				const defaultInitialProctoringOption = defaultProfile?.proctoringOption ?? defaultProctoringOption;

				const quizId = quizInfo?.quizId;
				let getProctoringAssessmentConfigurationResp: ProctoringAssessmentConfigurationV2 | null = null;
				if (quizId) {
					const quizInfoWithId = {
						...quizInfo,
						quizId,
					};
					getProctoringAssessmentConfigurationResp = await getProctoringAssessmentConfiguration(quizInfoWithId);
				}

				if (getProctoringAssessmentConfigurationResp && quizId) {
					const { proctoringOptionProfile, ...configuration } = getProctoringAssessmentConfigurationResp;
					const convertedConfiguration = convertProctoringAssessmentConfiguration(
						configuration,
						forceThirdParty,
						initialPlatformId,
					);
					if (initialEnabledValue === undefined) {
						setInitialEnabledValue(convertedConfiguration.enabled);
					}
					setInitialProctoringAssessmentConfiguration({ ...convertedConfiguration });
					setProctoringAssessmentConfiguration(convertedConfiguration);
					setInitialProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
					setProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
					setCurrentProfileId(proctoringOptionProfile.id);
					onInitialOptions?.(configuration, proctoringOptionProfile.proctoringOption);

					/**
					 * If we have initial options, and Verity is enabled, validate the configuration
					 */
					if (convertedConfiguration.enabled) {
						try {
							const { didValidate, ...validations } = await validateAssessment({
								courseId: quizInfo.courseId,
								quizId,
							});

							if (!didValidate) return;

							if (validations.isPasswordRequired && !validations.doPasswordsMatch) {
								coreToast.error(
									"The proctoring settings could not be validated. Please resave the settings for the assessment.",
									{
										containerId: `toast-${quizInfo.quizId}`,
									},
								);
							}
						} catch (e) {
							void logger?.error(e);
						}
					}
				} else {
					const convertedConfiguration = convertProctoringAssessmentConfiguration(
						{
							...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION,
							enabled: Boolean(proctoringAssessmentConfiguration && proctoringAssessmentConfiguration?.enabled),
						},
						forceThirdParty,
						initialPlatformId,
					);
					if (initialEnabledValue === undefined || !quizInfo.quizId) {
						setInitialEnabledValue(convertedConfiguration.enabled);
					}
					if (convertedConfiguration.enabled && proctoringAssessmentConfiguration?.password) {
						convertedConfiguration.password = proctoringAssessmentConfiguration.password;
					}
					setInitialProctoringAssessmentConfiguration(
						proctoringAssessmentConfiguration ?? { ...convertedConfiguration },
					);
					setProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? convertedConfiguration);
					setInitialProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
					setProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
					setCurrentProfileId(confirmedOption?.profileId ?? initialProctoringOption?.profileId ?? defaultProfile?.id);

					const pendingSrc = confirmedOption?.pendingLiveSessions ?? initialProctoringOption?.pendingLiveSessions;
					const hasPendingLiveSessions = pendingSrc && Object.keys(pendingSrc).length > 0;

					if (hasPendingLiveSessions) {
						setPendingLiveSessions(pendingSrc);
						const sortedIds = Object.values(pendingSrc).map((session) => session.identifier);
						setPendingLiveSessionIds(sortedIds);
					}

					onInitialOptions?.({}, confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
				}
			})();
		},
		[
			areProctoringProfilesEnforced,
			forceThirdParty,
			getDefaultProctoringProfile,
			getProctoringAssessmentConfiguration,
			getVerityInstituteSettings,
			initialEnabledValue,
			initialPlatformId,
			initialProctoringOption,
			logger,
			onInitialOptions,
			proctoringAssessmentConfiguration,
			quizInfo,
			validateAssessment,
		],
	);

	const handleEnabledChange = useCallback(
		async (enabled: boolean) => {
			if (!proctoringAssessmentConfiguration || !quizInfo.courseId) return false;
			proctoringAssessmentConfiguration.enabled = enabled;
			proctoringAssessmentConfiguration.password = proctoringAssessmentConfiguration.password
				? proctoringAssessmentConfiguration.password
				: quizInfo.quizId
					? password.generateKey()
					: null;
			try {
				if (proctoringAssessmentConfiguration) {
					setProctoringAssessmentConfiguration({ ...proctoringAssessmentConfiguration });
				}
				await onEnabledChange(proctoringAssessmentConfiguration, () => undefined);
			} catch (err) {
				console.error(err);
				alert("Something went wrong.");
			}
			return true;
		},
		[onEnabledChange, proctoringAssessmentConfiguration, quizInfo.courseId, quizInfo.quizId],
	);

	const pendingLiveSessionsArray = useMemo(() => {
		return pendingLiveSessionIds.map((id) => pendingLiveSessions[id]);
	}, [pendingLiveSessionIds, pendingLiveSessions]);

	return {
		state: {
			enforcedProfilesOnly,
			defaultProctoringOption,
			initialEnabledValue,
			initialProctoringAssessmentConfiguration,
			proctoringAssessmentConfiguration,
			initialProctoringOption,
			proctoringOption,
			currentProfileId,
			pendingLiveSessions,
			pendingLiveSessionIds,
			pendingLiveSessionsArray,
			existingLiveSessions,
		},
		action: {
			refreshSettings,
			setProctoringAssessmentConfiguration,
			setProctoringOption,
			setPendingLiveSessions,
			setPendingLiveSessionIds,
			setCurrentProfileId,
			setExistingLiveSessions,
			handleEnabledChange,
			setInitialEnabledValue,
		},
	};
}

export type UseEditQuizWrapperReturn = ReturnType<typeof useEditQuizWrapper>;
